import { Flex, Box, Tooltip } from '@chakra-ui/react';
import { reportConfigs } from './CustomReportConfig';
import { ChartType } from '@/componentsV1/Charts/types';
import type { Options as HighchartsOptions } from 'highcharts';
import {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from 'react';
import { ReportMetricCard } from '../../components/ReportMetricCard';
import { ReportNpsScore } from '../../components/ReportNpsScore';
import { CardTileHeading } from '../../components/CardTileHeading';
import { ChartRenderer } from '@/componentsV1/Charts';
import React, { useRef } from 'react';
import { cloneDeep } from 'lodash-es';
import { ReportTable } from '../../components/ReportTable';
import { Virtuoso } from 'react-virtuoso'; // https://virtuoso.dev/hello/
import { NoDataFound } from '@/componentsV1/NoDataFound';
import { ReportEmpty } from '@/assets/icons/ats';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import './tileSelection.css';
import { KText } from '@/ui-library/components/text/KText';
import { useKFlags } from '@/hooks/use-k-flags';

const CHART_HIDE_AXES_OPTIONS: HighchartsOptions = {
  legend: { reversed: false },
  yAxis: {
    title: { text: null },
    labels: { enabled: false },
    lineWidth: 0,
    gridLineWidth: 0,
    reversedStacks: false,
  },
  xAxis: {
    title: { text: null },
    labels: { enabled: false },
    lineWidth: 0,
    gridLineWidth: 0,
  },
};

export const TitlesContainer = forwardRef(({ report, ...props }: any, ref) => {
  const flags = useKFlags();
  const clonedReportConfigs = cloneDeep(reportConfigs({ flags }));
  const [tiles, setTiles] = useState<any[]>(clonedReportConfigs);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedTileKeys, setSelectedTileKeys] =
    useState<any[]>(clonedReportConfigs);
  const [containerBounds, setContainerBounds] = useState({
    height: 0,
    width: 0,
  });
  const [hasSearchTerm, setHasSearchTerm] = useState(false);
  const scrollHash = useMemo(() => {
    const scrollHashItems: Record<string, number> = {};
    tiles.forEach((tile, index) => {
      if (tile?.componentReference === 'title') {
        scrollHashItems[tile.id] = index;
      }
    });
    return scrollHashItems;
  }, [tiles]);
  const virtuosoRef = useRef(null);

  const updateSelected = (selected, parentIndex, childIndex) => {
    const parentItem = tiles[parentIndex];
    const changedItem =
      childIndex >= 0
        ? {
            ...parentItem,
            children: parentItem.children.map((child, i) =>
              i === childIndex ? { ...child, selected } : child
            ),
          }
        : { ...parentItem, selected };

    const updatedItems = tiles.map((item, index) =>
      index === parentIndex ? { ...item, ...changedItem } : item
    );
    setTiles(updatedItems);

    // Use updatedItems so onChange gets the new selection immediately (setState is async)
    const { selectedItems, deletedIds } =
      getSelectedTilesFromTiles(updatedItems);
    props?.onChange?.(selectedItems, deletedIds);
  };

  const updatSearchSelected = (selected, parentIndex) => {
    const parentItem = searchResults[parentIndex];
    const changedItem = { ...parentItem, selected };

    const updatedItems = searchResults.map((item, index) =>
      index === parentIndex ? { ...item, ...changedItem } : item
    );
    setSearchResults(updatedItems);
  };

  const initiateManualSelection = (e) => {
    const target = e.target;
    if (!target.closest('.custom_checkbox')) {
      const currentTarget = e.currentTarget;
      const checkbox = currentTarget.querySelector('.custom_checkbox');
      if (checkbox) {
        checkbox.click();
      }
    }
  };

  const getTileComponent = (
    tile,
    parentIndex = 0,
    childIndex = 0,
    searchItems = {}
  ) => {
    const { forSearch, searchSelectedIndex } = searchItems as any;
    const tooltipProps = getTooltipProps(tile) as any;
    switch (tile.componentReference) {
      case 'card':
        return (
          <ReportSelectionContext.Provider
            value={{
              consumer: 'DEFAULT',
              truncationLength: tile.ui?.truncationLength || 0,
            }}
          >
            <ReportMetricCard
              onCardClick={(e) => {
                if (!tile?.serverSelected) {
                  initiateManualSelection(e);
                }
              }}
              key={tile.id}
              data={tile.ui}
              selectConfig={{
                isChecked: tile.selected,
                onChange: (checked: boolean) => {
                  if (!tile?.serverSelected) {
                    // if it's already selected, we won't allow it to be unselected
                    updateSelected(checked, parentIndex, childIndex);
                    if (forSearch) {
                      updatSearchSelected(checked, searchSelectedIndex);
                    }
                  }
                },
              }}
              isSelect
              containerProps={getCardCSSProps(tile)}
              tooltipProps={tooltipProps}
            />
          </ReportSelectionContext.Provider>
        );
      case 'title':
        return (
          <Box
            background={'#FAFBFD'}
            padding="12px 20px"
            borderTop={tile.label === 'Pipelines' ? '0px' : '1px solid'}
            borderBottom="1px solid"
            borderColor={'border-color-header'}
            key={tile.id}
            id={tile.id}
            {...tile.cssProps}
          >
            <Box>
              <KText
                display={'inline-block'}
                paddingLeft="10px"
                size="sm-semi-bold"
                color="secondary-900"
              >
                {tile.label}
              </KText>
              <KText
                border={'1px solid'}
                borderColor={'border-color'}
                background="#fff"
                borderRadius={6}
                padding="0px 6px"
                marginLeft="10px"
                display={'inline-block'}
                color="secondary-700"
                size="sm-semi-bold"
              >
                {tile?.count ? tile.count : 0}
              </KText>
            </Box>
          </Box>
        );
      case 'nps_score':
        return (
          <ReportSelectionContext.Provider
            value={{
              consumer: 'DEFAULT',
              truncationLength: tile.ui?.truncationLength || 0,
            }}
          >
            <Tooltip {...tooltipProps}>
              <Flex
                className={getTileClassName(tile)}
                onClick={(e) => {
                  if (!tile?.serverSelected) {
                    initiateManualSelection(e);
                  }
                }}
                key={tile.id}
                border={'2px solid'}
                p="12px 20px 12px 20px"
                borderColor={tile.selected ? 'ats-primary' : 'border-color'}
                minW="430px"
                borderRadius="sm"
                flex={1}
                flexDir="column"
              >
                <ReportNpsScore
                  data={tile.ui}
                  tileData={tile}
                  selectConfig={{
                    isChecked: tile.selected,
                    onChange: (checked: boolean) => {
                      if (!tile?.serverSelected) {
                        updateSelected(checked, parentIndex, childIndex);
                        if (forSearch) {
                          updatSearchSelected(checked, searchSelectedIndex);
                        }
                      }
                    },
                  }}
                  isSelect
                />
              </Flex>
            </Tooltip>
          </ReportSelectionContext.Provider>
        );
      case 'chart':
        return (
          <Tooltip {...tooltipProps}>
            <Flex
              className={getTileClassName(tile)}
              onClick={(e) => {
                if (!tile?.serverSelected) {
                  initiateManualSelection(e);
                }
              }}
              key={tile.id}
              border={'2px solid'}
              p="12px 20px 12px 20px"
              borderColor={tile.selected ? 'ats-primary' : 'border-color'}
              minW="430px"
              borderRadius="sm"
              flex={1}
              flexDir="column"
            >
              <CardTileHeading
                checkboxId={tile.id}
                label={tile.ui.title}
                hoverLabel={tile.ui.title}
                selectConfig={{
                  isChecked: tile.selected,
                  onChange: (checked: boolean) => {
                    if (!tile?.serverSelected) {
                      // if it's already selected, we won't allow it to be unselected
                      updateSelected(checked, parentIndex, childIndex);
                      if (forSearch) {
                        updatSearchSelected(checked, searchSelectedIndex);
                      }
                    }
                  },
                }}
              />
              <ChartRenderer
                data={tile.ui.chartData}
                width={
                  tile.ui.chartType === 'pie' ||
                  tile.ui.chartType === 'horizontalStackedBar'
                    ? '100%'
                    : '80%'
                }
                height={tile.ui.chartType === 'pie' ? '300px' : '300px'}
                chartType={tile.ui.chartType as ChartType}
                highchartsOptions={
                  tile.ui.hideAxisValues ? CHART_HIDE_AXES_OPTIONS : undefined
                }
              />
            </Flex>
          </Tooltip>
        );
      case 'table':
        return (
          <ReportSelectionContext.Provider
            value={{ consumer: 'TILE_SELECTION', truncationLength: 0 }}
          >
            <Tooltip {...tooltipProps}>
              <Flex
                className={getTileClassName(tile)}
                onClick={(e) => {
                  if (!tile?.serverSelected) {
                    initiateManualSelection(e);
                  }
                }}
                key={tile.id}
                border={'2px solid'}
                p="12px 20px 12px 20px"
                borderColor={tile.selected ? 'ats-primary' : 'border-color'}
                minW="430px"
                borderRadius="sm"
                flex={1}
                flexDir="column"
                gap="12px"
              >
                <CardTileHeading
                  checkboxId={tile.id}
                  label={tile.ui.title}
                  hoverLabel={tile.ui.title}
                  selectConfig={{
                    isChecked: tile.selected,
                    onChange: (checked: boolean) => {
                      if (!tile?.serverSelected) {
                        // if it's already selected, we won't allow it to be unselected
                        updateSelected(checked, parentIndex, childIndex);
                        if (forSearch) {
                          updatSearchSelected(checked, searchSelectedIndex);
                        }
                      }
                    },
                  }}
                />
                <ReportTable
                  data={tile?.ui?.tableData}
                  tile={tile}
                  isAddBlock
                  unsupportedFilterLabels={[]}
                />
              </Flex>
            </Tooltip>
          </ReportSelectionContext.Provider>
        );
    }
  };

  const getTooltipProps = (tile) => {
    return {
      label: tile?.serverSelected
        ? 'Remove blocks by deleting them directly from the report'
        : '',
      borderRadius: 'sm',
      placement: 'top',
      maxW: '220px',
      boxShadow: 'ats-shadow-1',
      // className: 'cr-block',
    };
  };

  const getTileClassName = (tile) => {
    const className = `${tile.containerType} ${
      tile.serverSelected ? 'server-selected' : ''
    }`;
    return className;
  };

  const getCardCSSProps = (tile) => {
    const className = getTileClassName(tile);
    return { ...tile.cssProps, className };
  };

  const applyDefaultSelection = () => {
    const selectedTiles = report?.tiles || [];
    const selectedKeys = selectedTiles.map((tile) => {
      return tile?.metadata?.id;
    });
    setSelectedTileKeys(selectedKeys); // selected tiles FE keys

    const updatedItems = tiles.map((tileItr) => {
      const hasChildren = tileItr?.children?.length > 0;
      if (hasChildren) {
        const children = tileItr.children.map((child) => {
          if (selectedKeys.includes(child?.id)) {
            return { ...child, selected: true, serverSelected: true };
          }
          return child;
        });
        return { ...tileItr, children };
      } else {
        if (selectedKeys.includes(tileItr?.id)) {
          return { ...tileItr, selected: true, serverSelected: true };
        }
        return tileItr;
      }
    });
    setTiles(updatedItems);
  };

  const getSetContainerBounds = () => {
    const container = document.getElementById('tiles_container');
    if (!container) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    setContainerBounds(bounds);
  };

  useEffect(() => {
    // Use a timeout to ensure the component is fully rendered
    const initializeComponent = () => {
      getSetContainerBounds();
      applyDefaultSelection();
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeComponent, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  const setTilesBySearch = (searchTerm) => {
    const hasSearchTerm = searchTerm?.length > 0;
    searchTerm = searchTerm.toLowerCase();
    setHasSearchTerm(hasSearchTerm);
    if (hasSearchTerm) {
      setTempSearchResults(searchTerm);
    }
  };

  /**
   * Creates a flattened array of search results based on the search term
   * @param searchTerm - The lowercase search term to filter tiles
   *
   * This function:
   * 1. Clones the current tiles to avoid mutating the original state
   * 2. Filters tiles and their children based on matching the search term
   *
   * 3. For matching items, adds parentIndex and childIndex to maintain reference to the original tile for selection/unselection
   * 4. Updates searchResults state with the filtered items
   */
  const setTempSearchResults = (searchTerm) => {
    const clonedTiles = cloneDeep(tiles);
    const newResults: any[] = [];
    clonedTiles.forEach((tile, parentIndex) => {
      if (tile?.componentReference === 'title') {
        return;
      }
      const hasChildren = tile?.children?.length > 0;
      if (hasChildren) {
        tile?.children.forEach((child, childIndex) => {
          let comparisonValue = child?.ui?.topLabel ?? child?.ui?.title;
          comparisonValue = comparisonValue?.toLowerCase();
          const searchMatched = comparisonValue?.includes(searchTerm);
          if (searchMatched) {
            child['parentIndex'] = parentIndex;
            child['childIndex'] = childIndex;
            newResults.push(child);
          }
        });
      } else {
        let comparisonValue = tile?.ui?.topLabel ?? tile?.ui?.title;
        comparisonValue = comparisonValue?.toLowerCase();
        const searchMatched = comparisonValue?.includes(searchTerm);
        if (searchMatched) {
          tile['parentIndex'] = parentIndex;
          tile['childIndex'] = -1;
          newResults.push(tile);
        }
      }
    });
    setSearchResults(newResults);
  };

  const setScrollIntoView = (tileID) => {
    const scrollIndex = scrollHash[tileID];

    const attemptScroll = (retryCount = 0) => {
      const maxRetries = 3;

      if (scrollIndex !== undefined && virtuosoRef?.current) {
        try {
          virtuosoRef.current.scrollToIndex({
            index: scrollIndex,
            align: 'start',
          });
        } catch {
          if (retryCount < maxRetries) {
            setTimeout(() => attemptScroll(retryCount + 1), 100);
          }
        }
      } else if (retryCount < maxRetries) {
        // If scroll hash is empty, retry after a short delay (e.g. tiles still loading)
        setTimeout(() => attemptScroll(retryCount + 1), 200);
      }
    };

    attemptScroll();
  };

  const getSelectedTilesFromTiles = (tilesToUse: any[]) => {
    /*
		Returns the selected tiles and deleted tile IDs from a given tiles array.
		Used with current state (getSelectedTiles) or with pending updates (updateSelected)
		so that onChange receives the selection based on the latest selection state.*/
    const selectedItems: any[] = [];
    const deletedKeys: string[] = [];

    const addOrRemoveItems = (tile: any) => {
      const addedTileID = tile?.id;
      if (!selectedTileKeys.includes(addedTileID)) {
        const selectedItem = {
          ...tile.api,
          id: tile.id || tile.api?.metadata?.id,
          title: tile.ui?.title || tile.api?.metadata?.title,
        };
        selectedItems.push(selectedItem);
      }
    };

    const checkForDeletedKeys = (tile: any) => {
      const tileID = tile?.id;
      if (selectedTileKeys.includes(tileID)) {
        deletedKeys.push(tileID);
      }
    };

    tilesToUse.forEach((tile: any) => {
      if (tile?.children?.length > 0) {
        tile.children.forEach((child: any) => {
          if (child.selected) {
            addOrRemoveItems(child);
          } else {
            checkForDeletedKeys(child);
          }
        });
      } else {
        if (tile.selected) {
          addOrRemoveItems(tile);
        } else {
          checkForDeletedKeys(tile);
        }
      }
    });

    const deletedIds: any[] = [];
    (report?.tiles ?? []).forEach((itr: any) => {
      if (deletedKeys.includes(itr.metadata?.tileKey)) {
        deletedIds.push(itr.id);
      }
    });

    return { selectedItems, deletedIds };
  };

  const getSelectedTiles = () => getSelectedTilesFromTiles(tiles);

  useImperativeHandle(ref, () => ({
    setTilesBySearch,
    setScrollIntoView,
    getSelectedTiles,
  }));

  if (hasSearchTerm) {
    if (searchResults.length === 0) {
      return (
        <Box display={'flex'} height={'100%'} alignItems={'center'}>
          <NoDataFound
            noDataIcon={ReportEmpty}
            label="No results found"
            subLabel="Please try searching with a different term."
          />
        </Box>
      );
    }
    // For each search result, we flatten the tiles structure to simplify the activity.
    // When a checkbox is clicked, we update both original selected tiles and the temporary search results.
    return (
      <>
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: `${containerBounds.height}px` }}
          data={searchResults}
          itemContent={(parentIndex, data) => (
            <>
              <Box p={'0px 20px'} mb={'15px'} key={parentIndex}>
                {getTileComponent(data, data?.parentIndex, data?.childIndex, {
                  forSearch: true,
                  searchSelectedIndex: parentIndex,
                })}
              </Box>
            </>
          )}
        />
      </>
    );
  }

  return (
    <>
      <Virtuoso
        onScroll={(e) => props?.onScroll(e)}
        ref={virtuosoRef}
        style={{ height: `${containerBounds.height}px` }}
        data={tiles}
        itemContent={(parentIndex, data) => (
          <>
            {data && data?.componentReference === 'container' && (
              <Box
                p={'0px 20px'}
                mb={'15px'}
                key={parentIndex}
                display={'flex'}
                gap={'15px'}
              >
                {data.children.map((tile, childIndex) => {
                  return (
                    <React.Fragment key={childIndex}>
                      {getTileComponent(tile, parentIndex, childIndex)}
                    </React.Fragment>
                  );
                })}
              </Box>
            )}
            {data && data?.componentReference !== 'container' && (
              <Box
                p={data?.componentReference === 'title' ? '0px' : '0px 20px'}
                mb={'15px'}
                key={parentIndex}
              >
                {getTileComponent(data, parentIndex, -1)}
              </Box>
            )}
          </>
        )}
      />
    </>
  );
});
